import { Effect } from 'effect';
import {
  type OkraClientOptions,
  type OkraEffectClient,
  OkraClientError,
  runOkraClient,
} from './client-runtime.js';
import type {
  Job,
  JobChunkList,
  JobChunkRow,
  JobChunkStatus,
  JobList,
  JobPageList,
  JobPageRow,
  JobPageStatus,
  JobProgress,
  JobStatus,
} from './jobs.js';

/**
 * [A.6.5a] Typed `/v1/jobs` client — promise facade over `HttpApiClient`.
 *
 * Consumers (the web dashboard's RTK Query endpoints today; any future app)
 * get one typed factory here instead of re-declaring URLs, query strings, and
 * response interfaces. The wire shape (`Job`, from `./jobs.js`) is decoded by
 * the effect client and then normalized into `JobListItem`, the stricter view
 * dashboard components were built against (absent wire fields become `null`).
 */

export type { JobChunkRow, JobChunkStatus, JobPageRow, JobPageStatus, JobProgress, JobStatus };
export { OkraClientError as JobsClientError };

/** Normalized job row — the dashboard's view of a job (was `api/types.ts`). */
export interface JobListItem {
  object: 'job';
  id: string;
  type?: string;
  job_id: string;
  job_type: string;
  job_label: string | null;
  status: JobStatus;
  document_id: string | null;
  file_name: string | null;
  run_id: string | null;
  workflow_name: string | null;
  workflow_id: string | null;
  engine_id: string | null;
  provider: string | null;
  model: string | null;
  progress_current: number;
  progress_total: number;
  progress?: JobProgress;
  pages_completed: number;
  pages_failed: number;
  pages_running: number;
  pages_pending: number;
  pages_total: number;
  chunks_completed: number;
  chunks_total: number;
  cost_usd: number;
  duration_ms: number | null;
  error: string | null;
  latest_error?: string | null;
  request?: unknown;
  result?: unknown;
  metadata?: Record<string, unknown>;
  created_at: number;
  updated_at: number;
  completed_at: number | null;
}

export interface JobListResponse {
  object: 'list';
  data: JobListItem[];
  has_more: boolean;
  next_cursor: string | null;
}

export interface JobChildListResponse<T> {
  object: 'list';
  job_id: string;
  run_id: string | null;
  data: T[];
  has_more: boolean;
  next_cursor: string | null;
}

export interface ListJobsParams {
  status?: JobStatus | 'all';
  limit?: number;
  /** Opaque `next_cursor` from the previous page; `null`/`undefined` starts at the first page. */
  cursor?: string | null;
  /** `false` skips live hydration of in-flight rows (index state only). */
  hydrate?: boolean;
}

export interface JobsClient {
  listJobs(params?: ListJobsParams): Promise<JobListResponse>;
  getJob(jobId: string): Promise<JobListItem>;
  listJobPages(params: {
    jobId: string;
    status?: JobPageStatus | 'failed';
    limit?: number;
  }): Promise<JobChildListResponse<JobPageRow>>;
  listJobChunks(params: {
    jobId: string;
    status?: JobChunkStatus | 'error';
    limit?: number;
  }): Promise<JobChildListResponse<JobChunkRow>>;
  cancelJob(jobId: string): Promise<JobListItem>;
  retryJob(jobId: string): Promise<JobListItem>;
  resumeJob(jobId: string): Promise<JobListItem>;
}

export interface JobsClientOptions extends OkraClientOptions {}

function normalizeJob(wire: Job): JobListItem {
  return {
    object: 'job',
    id: wire.id,
    ...(wire.type !== undefined ? { type: wire.type } : {}),
    job_id: wire.job_id,
    job_type: wire.job_type,
    job_label: wire.job_label ?? null,
    status: wire.status,
    document_id: wire.document_id ?? null,
    file_name: wire.file_name ?? null,
    run_id: wire.run_id ?? null,
    workflow_name: wire.workflow_name ?? null,
    workflow_id: wire.workflow_id ?? null,
    engine_id: wire.engine_id ?? null,
    provider: wire.provider ?? null,
    model: wire.model ?? null,
    progress_current: wire.progress_current,
    progress_total: wire.progress_total,
    ...(wire.progress !== undefined ? { progress: wire.progress } : {}),
    pages_completed: wire.pages_completed,
    pages_failed: wire.pages_failed,
    pages_running: wire.pages_running,
    pages_pending: wire.pages_pending,
    pages_total: wire.pages_total,
    chunks_completed: wire.chunks_completed,
    chunks_total: wire.chunks_total,
    cost_usd: wire.cost_usd ?? 0,
    duration_ms: wire.duration_ms ?? null,
    error: wire.error ?? null,
    ...(wire.latest_error !== undefined ? { latest_error: wire.latest_error } : {}),
    ...(wire.request !== undefined ? { request: wire.request } : {}),
    ...(wire.result !== undefined ? { result: wire.result } : {}),
    ...(wire.metadata !== undefined ? { metadata: wire.metadata } : {}),
    created_at: wire.created_at,
    updated_at: wire.updated_at,
    completed_at: wire.completed_at ?? null,
  };
}

function normalizeJobList(wire: JobList): JobListResponse {
  return {
    object: 'list',
    data: wire.data.map(normalizeJob),
    has_more: wire.has_more,
    next_cursor: wire.next_cursor,
  };
}

function normalizeChildList<T>(wire: JobPageList | JobChunkList): JobChildListResponse<T> {
  return {
    object: 'list',
    job_id: wire.job_id,
    run_id: wire.run_id,
    data: wire.data as T[],
    has_more: wire.has_more,
    next_cursor: wire.next_cursor,
  };
}

export function makeJobsClient(options: JobsClientOptions): JobsClient {
  const run = <A>(use: (client: OkraEffectClient) => Effect.Effect<A, unknown, never>) =>
    runOkraClient(options, use, 'Jobs request failed');

  return {
    listJobs: (params) =>
      run((client) =>
        client.jobs.listJobs({
          query: {
            ...(params?.status && params.status !== 'all' ? { status: params.status } : {}),
            ...(params?.limit !== undefined ? { limit: params.limit } : {}),
            ...(params?.cursor ? { cursor: params.cursor } : {}),
            ...(params?.hydrate === false ? { hydrate: '0' as const } : {}),
          },
        }),
      ).then(normalizeJobList),
    getJob: (jobId) =>
      run((client) => client.jobs.getJob({ params: { jobId } })).then(normalizeJob),
    listJobPages: ({ jobId, status, limit }) =>
      run(
        (client) =>
          client.jobs.listJobPages({
            params: { jobId },
            query: {
              ...(status ? { status } : {}),
              ...(limit !== undefined ? { limit } : {}),
            },
          }) as Effect.Effect<JobPageList, unknown, never>,
      ).then((wire) => normalizeChildList<JobPageRow>(wire)),
    listJobChunks: ({ jobId, status, limit }) =>
      run(
        (client) =>
          client.jobs.listJobChunks({
            params: { jobId },
            query: {
              ...(status ? { status } : {}),
              ...(limit !== undefined ? { limit } : {}),
            },
          }) as Effect.Effect<JobChunkList, unknown, never>,
      ).then((wire) => normalizeChildList<JobChunkRow>(wire)),
    cancelJob: (jobId) =>
      run((client) => client.jobs.cancelJob({ params: { jobId } })).then(normalizeJob),
    retryJob: (jobId) =>
      run((client) => client.jobs.retryJob({ params: { jobId } })).then(normalizeJob),
    resumeJob: (jobId) =>
      run((client) => client.jobs.resumeJob({ params: { jobId } })).then(normalizeJob),
  };
}
